import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Paper, Stack, Typography, Tooltip, Chip } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TodayIcon from '@mui/icons-material/Today';
import { fetchCalendarEvents } from '../../controllers/clubEventsController';
import { searchMeetingsByDateRange } from '../../controllers/meetingsController';
import { useNavigate } from 'react-router-dom';
import {
  parseApiLocalDateTime,
  toTurkeyLocalApiStringEndOfDay,
  toTurkeyLocalApiStringStartOfDay,
} from '../../utils/turkeyLocalDateTime';

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);

const weekdays = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const ClubEventsCalendar = () => {
  const [anchor, setAnchor] = useState(() => startOfMonth(new Date()));
  const [items, setItems] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const range = useMemo(() => {
    const start = startOfMonth(anchor);
    const end = endOfMonth(anchor);
    return { start, end };
  }, [anchor]);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchCalendarEvents({
          start: toTurkeyLocalApiStringStartOfDay(range.start),
          end: toTurkeyLocalApiStringEndOfDay(range.end),
          includeInactive: true,
        });
        if (!active) return;
        setItems(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!active) return;
        setItems([]);
      }

      try {
        const meetingsResult = await searchMeetingsByDateRange({
          startDate: toTurkeyLocalApiStringStartOfDay(range.start),
          endDate: toTurkeyLocalApiStringEndOfDay(range.end),
          pageNumber: 1,
          pageSize: 500,
          isActive: true,
        });
        if (!active) return;
        setMeetings(Array.isArray(meetingsResult.data) ? meetingsResult.data : []);
      } catch (e) {
        if (!active) return;
        setMeetings([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [range.start, range.end]);

  const days = useMemo(() => {
    const start = new Date(range.start);
    const end = new Date(range.end);
    const result = [];
    // Align to Monday as first day (Pzt)
    let cur = new Date(start);
    const weekdayIndex = (cur.getDay() + 6) % 7; // 0=Pzt
    cur.setDate(cur.getDate() - weekdayIndex);
    while (cur <= end || result.length % 7 !== 0) {
      result.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return result;
  }, [range.start, range.end]);

  const dateKey = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const byDate = useMemo(() => {
    const map = new Map();
    items.forEach((e) => {
      const start = parseApiLocalDateTime(e.startDate);
      if (!start) return;
      const k = dateKey(start);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push({ ...e, _type: 'event' });
    });
    meetings.forEach((m) => {
      const start = parseApiLocalDateTime(m.meetingDate || m.startDate || m.startTime || m.date);
      if (!start) return;
      const k = dateKey(start);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push({ ...m, _type: 'meeting' });
    });
    return map;
  }, [items, meetings]);

  const monthTitle = anchor.toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' });

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography sx={{ fontSize: 18, fontWeight: 500 }}>{monthTitle}</Typography>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Önceki Ay">
            <IconButton
              size="small"
              onClick={() => setAnchor((d) => addMonths(d, -1))}
              sx={{
                backgroundColor: '#E7EDF4',
                borderRadius: '6px',
                width: 32,
                height: 32,
                '&:hover': {
                  backgroundColor: '#CFDBEA',
                },
                '& svg': {
                  fontSize: '14px',
                  color: '#0E4D94',
                },
              }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Bugün">
            <IconButton
              size="small"
              onClick={() => setAnchor(startOfMonth(new Date()))}
              sx={{
                backgroundColor: '#E7EDF4',
                borderRadius: '6px',
                width: 32,
                height: 32,
                '&:hover': {
                  backgroundColor: '#CFDBEA',
                },
                '& svg': {
                  fontSize: '14px',
                  color: '#0E4D94',
                },
              }}
            >
              <TodayIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Sonraki Ay">
            <IconButton
              size="small"
              onClick={() => setAnchor((d) => addMonths(d, 1))}
              sx={{
                backgroundColor: '#E7EDF4',
                borderRadius: '6px',
                width: 32,
                height: 32,
                '&:hover': {
                  backgroundColor: '#CFDBEA',
                },
                '& svg': {
                  fontSize: '14px',
                  color: '#0E4D94',
                },
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
        {weekdays.map((w) => (
          <Typography
            key={w}
            sx={{
              px: 1,
              py: 1,
              fontSize: '13px',
              fontWeight: 600,
              color: '#666666',
              textAlign: 'center',
            }}
          >
            {w}
          </Typography>
        ))}
        {days.map((d) => {
          const key = dateKey(d);
          const list = byDate.get(key) || [];
          const isOtherMonth = d.getMonth() !== anchor.getMonth();
          const isToday = dateKey(d) === dateKey(new Date());
          return (
            <Paper
              key={key + Math.random()}
              sx={{
                minHeight: 100,
                p: 1,
                border: '1px solid #e6e6e6',
                boxShadow: 'none',
                borderRadius: '8px',
                bgcolor: isOtherMonth ? '#fafafa' : '#ffffff',
                position: 'relative',
                '&:hover': {
                  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: isToday ? 600 : 500,
                  color: isToday ? '#0E4D94' : isOtherMonth ? '#a3a3a3' : '#444444',
                  mb: 0.5,
                }}
              >
                {d.getDate()}
              </Typography>
              <Stack spacing={0.5}>
                {list.slice(0, 3).map((e) => (
                  <Box
                    key={e.id}
                    onClick={() =>
                      e._type === 'meeting'
                        ? navigate(`/events/weekly-meetings/${e.id}`)
                        : navigate(`/events/${e.id}`)
                    }
                    sx={{
                      cursor: 'pointer',
                      px: 0.75,
                      py: 0.5,
                      borderRadius: '6px',
                      backgroundColor:
                        e._type === 'meeting'
                          ? '#E8F5E9'
                          : e.isActive
                          ? '#E7EDF4'
                          : '#FFF4E6',
                      border:
                        e._type === 'meeting'
                          ? '1px solid #C8E6C9'
                          : `1px solid ${e.isActive ? '#CFDBEA' : '#FFE0B2'}`,
                      '&:hover': {
                        backgroundColor:
                          e._type === 'meeting'
                            ? '#C8E6C9'
                            : e.isActive
                            ? '#CFDBEA'
                            : '#FFE0B2',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '11px',
                        fontWeight: 500,
                        color:
                          e._type === 'meeting'
                            ? '#2E7D32'
                            : e.isActive
                            ? '#0E4D94'
                            : '#FF9800',
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {e.title}
                    </Typography>
                  </Box>
                ))}
                {list.length > 3 && (
                  <Typography
                    sx={{
                      fontSize: '11px',
                      color: '#a3a3a3',
                      fontWeight: 500,
                    }}
                  >
                    +{list.length - 3} daha
                  </Typography>
                )}
              </Stack>
            </Paper>
          );
        })}
      </Box>
    </Stack>
  );
};

export default ClubEventsCalendar;


